import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CommandLine from '@/components/ai/CommandLine';
import AIAssistant from '@/components/ai/AIAssistant';
import AutomationControl from '@/components/ai/AutomationControl';
import DeploymentCenter from '@/components/ai/DeploymentCenter';
import NetworkManager from '@/components/ai/NetworkManager';
import SecurityAuditPopup from '@/components/ai/SecurityAuditPopup';
import { Brain, Terminal, Server, Network, Cloud, Zap, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from "sonner";
import { generateGrokResponse } from '@/utils/grokApiClient';

interface LocationState {
  activateTab?: string;
  task?: string;
}

const AIAutomation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  
  const [activeTab, setActiveTab] = useState<string>('automations');
  const [pendingTask, setPendingTask] = useState<string | null>(null);
  const [taskDescription, setTaskDescription] = useState<string | null>(null);
  const [completedTasks, setCompletedTasks] = useState<string[]>([]);
  
  // Security Audit state
  const [isSecurityAuditOpen, setIsSecurityAuditOpen] = useState(false);
  const [selectedAuditType, setSelectedAuditType] = useState('gao');
  const [auditInProgress, setAuditInProgress] = useState(false);
  const [auditProgress, setAuditProgress] = useState(0);
  const [auditCompleted, setAuditCompleted] = useState(false);
  const [auditSteps, setAuditSteps] = useState<{
    id: string;
    title: string;
    description: string;
    status: 'pending' | 'inProgress' | 'completed';
    progress: number;
  }[]>([
    {
      id: 'step1',
      title: 'Infrastructure Scanning',
      description: 'Scanning network infrastructure for vulnerabilities',
      status: 'pending',
      progress: 0
    },
    {
      id: 'step2',
      title: 'Configuration Analysis',
      description: 'Analyzing device and network configurations',
      status: 'pending',
      progress: 0
    },
    {
      id: 'step3',
      title: 'Security Policy Review',
      description: 'Reviewing security policies and compliance',
      status: 'pending',
      progress: 0
    },
    {
      id: 'step4',
      title: 'Access Control Verification',
      description: 'Verifying access controls and permissions',
      status: 'pending',
      progress: 0
    },
    {
      id: 'step5',
      title: 'Report Generation',
      description: 'Generating comprehensive security report',
      status: 'pending',
      progress: 0
    }
  ]);

  useEffect(() => {
    // Process any incoming tasks from other pages
    if (state?.task) {
      setPendingTask(state.task);
      handleRunAutoTask(state.task);
      
      // Get a description for this task from S2AI/GROK
      getTaskDescription(state.task);
      
      // Clear the state to prevent re-triggering
      navigate(location.pathname, { replace: true });
    }
    
    // Handle tab activation from navigation
    if (state?.activateTab) {
      setActiveTab(state.activateTab);
    }
  }, [state, location.pathname, navigate]);
  
  // Effect to progress the security audit when it's in progress
  useEffect(() => {
    if (!auditInProgress) return;
    
    let intervalId: ReturnType<typeof setInterval>;
    let stepIndex = 0;
    let currentProgress = 0;
    
    const updateStepProgress = () => {
      setAuditSteps(prev => {
        // Create a copy to avoid mutation
        const updated = [...prev];
        
        // Update the current step's progress
        if (stepIndex < updated.length) {
          const step = updated[stepIndex];
          const newProgress = Math.min(100, step.progress + 5);
          
          updated[stepIndex] = {
            ...step,
            status: 'inProgress',
            progress: newProgress
          };
          
          // If this step is complete, mark it as completed and move to next step
          if (newProgress >= 100) {
            updated[stepIndex] = {
              ...updated[stepIndex],
              status: 'completed',
              progress: 100
            };
            
            // Move to next step if available
            if (stepIndex < updated.length - 1) {
              stepIndex += 1;
            }
          }
        }
        
        return updated;
      });
      
      // Update overall progress
      currentProgress = Math.min(100, currentProgress + 2);
      setAuditProgress(currentProgress);
      
      // Check if we're done
      if (currentProgress >= 100) {
        clearInterval(intervalId);
        setAuditInProgress(false);
        setAuditCompleted(true);
        
        // Mark all steps as complete
        setAuditSteps(prev => prev.map(step => ({
          ...step,
          status: 'completed',
          progress: 100
        })));
        
        // Add to completed tasks
        setCompletedTasks(prev => {
          if (!prev.includes('Security Audit')) {
            return [...prev, 'Security Audit'];
          }
          return prev;
        });
        
        toast.success("Security Audit Complete", {
          description: "The security audit has finished successfully. AI-enhanced report is ready to download."
        });
      }
    };
    
    // Update progress every 250ms for smoother animation
    intervalId = setInterval(updateStepProgress, 250);
    
    return () => clearInterval(intervalId);
  }, [auditInProgress]);

  const getTaskDescription = async (task: string) => {
    try {
      // Use the GROK API to get an intelligent description of this task
      const description = await generateGrokResponse(`Describe the ${task} automation task`);
      setTaskDescription(description);
    } catch (error) {
      console.error("Error getting task description:", error);
    }
  };

  const handleRunAutoTask = (task: string) => {
    toast.success(`AI Task Started: ${task}`, {
      description: "The S2AI with GROK is now processing your request. Results will appear shortly."
    });
    
    // Determine which tab should be active based on the task
    const taskLower = task.toLowerCase();
    
    if (taskLower.includes('network') || taskLower.includes('bandwidth')) {
      setActiveTab('networks');
    } else if (taskLower.includes('deploy') || taskLower.includes('create')) {
      setActiveTab('deployments');
    } else if (taskLower.includes('command') || taskLower.includes('terminal')) {
      setActiveTab('terminal');
    } else if (taskLower.includes('security') || taskLower.includes('audit')) {
      // Handle security audit task
      setIsSecurityAuditOpen(true);
    } else {
      setActiveTab('automations');
    }
  };

  const handleTaskComplete = (taskName: string) => {
    // For Security Audit, open the audit dialog instead of showing progress popup
    if (taskName === 'Security Audit') {
      // Reset the audit state to make sure we're starting fresh
      resetAuditState();
      setIsSecurityAuditOpen(true);
      return;
    }
    
    toast.success(`Task Complete: ${taskName}`, {
      description: "The S2AI with GROK has successfully completed your request."
    });
    
    // Add to completed tasks if not already there
    if (!completedTasks.includes(taskName)) {
      setCompletedTasks(prev => [...prev, taskName]);
    }
  };
  
  // Security Audit handlers
  const resetAuditState = () => {
    setAuditProgress(0);
    setAuditSteps(prev => prev.map(step => ({ 
      ...step, 
      status: 'pending', 
      progress: 0 
    })));
    setAuditCompleted(false);
    setAuditInProgress(false);
  };
  
  const handleStartAudit = () => {
    // Reset audit state before starting
    resetAuditState();
    setAuditInProgress(true);
    
    toast.info(`Starting ${selectedAuditType.toUpperCase()} security audit with enhanced AI analysis`, {
      description: "The audit will take a few moments to complete."
    });
  };
  
  // Update the handleDownloadReport function to ensure it works properly with the SecurityAuditPopup
  const handleDownloadReport = () => {
    // Log the download request for debugging
    console.log("AI-enhanced report download requested from AIAutomation component");
    
    // Show a more detailed notification to help the user understand what's happening
    toast.success("AI-enhanced report download initiated", {
      description: "Your security audit report is being prepared by GROK AI. Check your downloads folder if it doesn't appear automatically.",
      duration: 5000, // Show for 5 seconds
    });
    
    // Add a backup notification after a short delay in case the download doesn't start
    setTimeout(() => {
      toast.info("Download taking longer than expected?", {
        description: "If you don't see the download, check your browser's download settings or try a different browser.",
        duration: 8000,
      });
    }, 10000); // Show this after 10 seconds if download seems stuck
  };

  const getCategoryForTask = (taskName: string): string => {
    const taskLower = taskName.toLowerCase();
    if (taskLower.includes('network') || taskLower.includes('bandwidth')) {
      return 'network';
    } else if (taskLower.includes('security') || taskLower.includes('audit')) {
      return 'security';
    } else if (taskLower.includes('resource') || taskLower.includes('scale')) {
      return 'resources';
    }
    return 'general';
  };

  const quickTasks = [
    { name: "Network Optimization", icon: <Network className="mr-2 h-4 w-4" />, category: "network" },
    { name: "Security Audit", icon: <Shield className="mr-2 h-4 w-4" />, category: "security" },
    { name: "Resources Scale", icon: <Cloud className="mr-2 h-4 w-4" />, category: "resources" },
    { name: "Bandwidth Analysis", icon: <Zap className="mr-2 h-4 w-4" />, category: "network" }
  ];

  return (
    <div className="container mx-auto py-6 space-y-6 relative">
      <Helmet>
        <title>AI Automation | Network Management</title>
      </Helmet>
      
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight flex items-center">
          <Brain className="mr-2 h-8 w-8 text-primary" />
          AI Network Automation
          <span className="text-sm font-normal ml-2 text-muted-foreground">powered by S2AI with GROK</span>
        </h1>
        <p className="text-muted-foreground">
          Manage your networks and infrastructure through AI-powered automation
        </p>
      </div>
      
      {taskDescription && (
        <Card className="bg-muted/50 border-primary/20">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium mb-1">S2AI Task Analysis:</h3>
            <p className="text-sm text-muted-foreground">{taskDescription}</p>
          </CardContent>
        </Card>
      )}
      
      <div className="flex flex-wrap gap-3 mb-3">
        {quickTasks.map((task) => (
          <Button 
            key={task.name}
            variant={completedTasks.includes(task.name) ? "outline" : "default"}
            className="flex items-center relative"
            onClick={() => handleTaskComplete(task.name)}
          >
            {task.icon}
            {task.name}
            {completedTasks.includes(task.name) && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border border-white"></span>
            )}
          </Button>
        ))}
      </div>
      
      <Tabs defaultValue="automations" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="automations" className="flex items-center">
            <Brain className="mr-2 h-4 w-4" />
            Automation
          </TabsTrigger>
          <TabsTrigger value="networks" className="flex items-center">
            <Network className="mr-2 h-4 w-4" />
            Networks
          </TabsTrigger>
          <TabsTrigger value="deployments" className="flex items-center">
            <Server className="mr-2 h-4 w-4" />
            Deployments
          </TabsTrigger>
          <TabsTrigger value="terminal" className="flex items-center">
            <Terminal className="mr-2 h-4 w-4" />
            Command Line
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="automations" className="space-y-6">
          <AutomationControl initialTask={pendingTask} />
        </TabsContent>
        
        <TabsContent value="networks" className="space-y-6">
          <NetworkManager />
        </TabsContent>
        
        <TabsContent value="deployments" className="space-y-6">
          <DeploymentCenter initialTask={pendingTask} />
        </TabsContent>
        
        <TabsContent value="terminal" className="space-y-6">
          <Card className="h-[500px]">
            <CardContent className="p-0 h-full">
              <CommandLine initialCommand={pendingTask ? `s2ai analyze ${pendingTask}` : ''} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Security Audit Popup with AI-enhanced reports */}
      <SecurityAuditPopup
        isOpen={isSecurityAuditOpen}
        onClose={() => setIsSecurityAuditOpen(false)}
        overallProgress={auditProgress}
        onAuditTypeChange={setSelectedAuditType}
        selectedAuditType={selectedAuditType}
        steps={auditSteps}
        onDownloadReport={handleDownloadReport}
        onStartAudit={handleStartAudit}
        auditInProgress={auditInProgress}
        auditCompleted={auditCompleted}
      />
      
      {/* The AI Assistant will be rendered as a floating button in the bottom right */}
      <AIAssistant />
    </div>
  );
};

export default AIAutomation;
